import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '@/services/api';
import { LocalOut, EventOut } from '@/types/api';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import './Locals.scss';

const Locals: React.FC = () => {
  const { t } = useTranslation();

  const [locals, setLocals] = useState<LocalOut[]>([]);
  const [events, setEvents] = useState<EventOut[]>([]);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [assigningEventId, setAssigningEventId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editingLocalId, setEditingLocalId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingLocalId, setSavingLocalId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [localsData, eventsData] = await Promise.all([
        apiService.getLocals(),
        apiService.getMyManagedEvents(),
      ]);
      setLocals(localsData);
      setEvents(eventsData);
    } catch (err) {
      console.error('Failed to load locals/events:', err);
      setError(t('errors.general'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const created = await apiService.createLocal({ name: name.trim() });
      setLocals((prev) => [...prev, created]);
      setName('');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    } finally {
      setCreating(false);
    }
  };

  const startEditLocal = (local: LocalOut) => {
    setEditingLocalId(local.id);
    setEditingName(local.name);
  };

  const cancelEditLocal = () => {
    setEditingLocalId(null);
    setEditingName('');
  };

  const handleSaveLocalName = async (localId: number) => {
    if (!editingName.trim()) return;
    setSavingLocalId(localId);
    setError(null);
    try {
      const updated = await apiService.updateLocal(localId, { name: editingName.trim() });
      setLocals((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      setEditingLocalId(null);
      setEditingName('');
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    } finally {
      setSavingLocalId(null);
    }
  };

  const handleAssign = async (event: EventOut, localId: number | null) => {
    setAssigningEventId(event.id);
    setError(null);
    try {
      const updated = await apiService.updateEvent(event.id, { ...event, local_id: localId });
      setEvents((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('errors.general'));
    } finally {
      setAssigningEventId(null);
    }
  };

  const localOptions = locals.map((local) => ({ value: local.id, label: local.name }));

  return (
    <div className="locals-page">
      <h2 className="locals-page__title">{t('locals.title')}</h2>

      {error && <div className="locals-page__error">{error}</div>}

      <Card variant="elevated" className="locals-page__form-card">
        <h3>{t('locals.newLocal')}</h3>
        <form onSubmit={handleCreate} className="locals-page__form">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('locals.namePlaceholder') as string}
            required
          />
          <Button type="submit" variant="primary" disabled={creating} loading={creating}>
            {t('locals.create')}
          </Button>
        </form>
      </Card>

      {!loading && locals.length === 0 ? (
        <Card variant="elevated">
          <p>{t('locals.empty')}</p>
        </Card>
      ) : (
        <div className="locals-page__list">
          {locals.map((local) => (
            <Card key={local.id} variant="elevated" className="locals-page__item">
              {editingLocalId === local.id ? (
                <div className="locals-page__edit-form">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    autoFocus
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={savingLocalId === local.id}
                    loading={savingLocalId === local.id}
                    onClick={() => handleSaveLocalName(local.id)}
                  >
                    {t('common.save')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={savingLocalId === local.id}
                    onClick={cancelEditLocal}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              ) : (
                <div className="locals-page__item-row">
                  <h4>{local.name}</h4>
                  <Button variant="ghost" size="sm" onClick={() => startEditLocal(local)}>
                    {t('common.edit')}
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Card variant="elevated" className="locals-page__assign-card">
        <h3>{t('locals.assignEvents')}</h3>
        <p className="locals-page__assign-hint">{t('locals.assignHint')}</p>
        <div className="locals-page__assign-table">
          {events.map((event) => (
            <div key={event.id} className="locals-page__assign-row">
              <span className="locals-page__assign-event-name">{event.name}</span>
              <SearchableSelect<number>
                className="locals-page__assign-select"
                placeholder={t('locals.noLocal')}
                clearable
                options={localOptions}
                value={event.local_id ?? null}
                disabled={assigningEventId === event.id}
                onChange={(value) => handleAssign(event, value)}
              />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default Locals;
